import { Form, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionDrawer } from '../../components/ActionDrawer';
import { ApiError } from '../../lib/api-client';
import { createCustomer, type CreateCustomerInput, type DuplicateMatch } from './api';
import { CustomerFormFields } from './CustomerFormFields';
import { DuplicateWarning } from './DuplicateWarning';

const { Text } = Typography;

export function CustomerCreateDrawer({
  open,
  onClose,
  onCreated,
  onOpenMatch,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (uid: string) => void;
  onOpenMatch: (uid: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [form] = Form.useForm<CreateCustomerInput>();
  const [submitting, setSubmitting] = useState(false);
  const [duplicates, setDuplicates] = useState<DuplicateMatch[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const submit = async (force: boolean): Promise<void> => {
    let values: CreateCustomerInput;
    try {
      values = await form.validateFields();
    } catch {
      // The form already shows what is wrong next to each field.
      return;
    }
    setSubmitting(true);
    setFailure(null);
    try {
      const created = await createCustomer({ ...values, confirmNotDuplicate: force });
      form.resetFields();
      setDuplicates(null);
      onCreated(created.uid);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'CUSTOMER_DUPLICATE_SUSPECTED') {
        setDuplicates(e.details['matches'] as DuplicateMatch[]);
        return;
      }
      setFailure(e instanceof ApiError ? e.message : t('customers.form.createFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionDrawer
      open={open}
      title={t('customers.create')}
      submitLabel={t('customers.create')}
      submitting={submitting}
      onSubmit={() => void submit(false)}
      onClose={() => {
        setDuplicates(null);
        setFailure(null);
        onClose();
      }}
      error={
        duplicates !== null ? (
          <DuplicateWarning
            matches={duplicates}
            onOpenMatch={onOpenMatch}
            onConfirmDifferent={() => void submit(true)}
          />
        ) : failure !== null ? (
          <Text type="danger">{failure}</Text>
        ) : undefined
      }
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <CustomerFormFields mode="create" />
      </Form>
    </ActionDrawer>
  );
}
